/**
 * The shapes the screens are drawn from. The server's own records (shared/agents.ts, shared/types.ts)
 * are turned into these in the store; nothing here is sample data.
 */
import type { MemoryKind } from '@shared/types'
import type { Palette } from './kit'

/** main = the first agent, which owns the conversations from before agents; it only chooses the hints on an empty page. */
export type AgentKind = 'lead' | 'custom'

export interface MemoryItem {
  id: string
  kind: 'rule' | 'about' | 'note' | 'learned'
  text: string
  when?: string
  /** The memory's own name on the server. */
  label?: string
}
/**
 * One thing every agent knows: who you are and how you like to be answered (always read), or a topic
 * — a strategy, a plan, a subject — that is read only when the conversation is about it. A strategy is
 * a topic like any other: it is never the whole of what the agents know.
 */
export interface Note {
  id: string
  section: 'You' | 'Topics' | 'Stocks'
  name: string
  text: string
  updated: string
  /** A topic can be put aside without being forgotten. */
  off?: boolean
  kind?: MemoryKind
  /** A note on a stock: since when and how long it holds, or when and why it ended. */
  span?: string
  ended?: boolean
}

/** An agent is whatever you describe it to be: a name, an optional label, a description. */
export interface Agent {
  id: string
  name: string
  label: string
  description: string
  palette: Palette
  kind: AgentKind
  skills: string[]
  /** What it is doing right now (null = free). */
  busy: string | null
  memory: MemoryItem[]
  lang: 'Türkçe' | 'English'
  notify: boolean
  /** Stands first in the tab row. */
  pinned?: boolean
  /** Kept out of the tab row; Search still finds it. */
  hidden?: boolean
  custom?: boolean
}
/** A folder only tidies the tab bar: its agents fold into one tab that widens when pressed. It changes nothing else, and an agent sits in at most one. */
export interface Folder {
  id: string
  name: string
  members: string[]
}
/** A group chat is yours: any name, any agents, as many as you like — one conversation with all of its members. */
export interface Group {
  id: string
  name: string
  members: string[]
}

/** A conversation in a list. What was said in it is loaded when it is opened (store.thread). */
export interface Session {
  id: string
  /** The agent or group chat it belongs to; null = a temporary chat. */
  agentId: string | null
  title: string
  /** The agents brought into a temporary chat. */
  with: string[]
  at: number
  /** The first words of its last answer. */
  preview: string
  busy: boolean
  pinned: boolean
  unread: boolean
  /** A conversation one agent opened with another. */
  wire: { from: string; to: string } | null
}

/** A routine: a task that wakes an agent in one of its conversations. */
export interface Routine {
  id: string
  name: string
  agentId: string
  /** What the agent does each time, in the words it was told (or wrote for itself). */
  instruction: string
  when: string
  next: string
  last: string
  result: 'ok' | 'quiet' | 'skipped' | 'running'
  on: boolean
  /** The conversation it wakes the agent in. */
  chatId?: string | null
}

/** A workspace: a separate place with its own agents and group chats. */
export interface Space {
  id: string
  name: string
  /** The mark's colours (a CSS gradient); empty = the product's own mark. */
  tint: string
}
export const TINTS = [
  'linear-gradient(160deg,#9fe8c8 0%,#7fb2d8 55%,#bfaef8 100%)',
  'linear-gradient(160deg,#ffd29a 0%,#ff9a76 55%,#ee82b4 100%)',
  'linear-gradient(160deg,#aec2f8 0%,#9868d2 60%,#ee82b4 100%)',
  'linear-gradient(160deg,#fbf7e6 0%,#d0d6d4 55%,#8c9aa0 100%)',
  'linear-gradient(160deg,#b6efcc 0%,#ffeaa6 55%,#ffae74 100%)',
]

export const EXAMPLE: Record<AgentKind, string> = {
  lead: 'Ask anything, or hand over a job…',
  custom: 'Ask anything, or hand over a job…',
}
